import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, LogOut, Mail, CalendarClock, Users, ShieldAlert, Package, Newspaper, Mic, LayoutDashboard, GraduationCap, CheckCircle2 } from "lucide-react";
import { API } from "../lib/api";
import AdminProducts from "../components/AdminProducts";
import AdminAcademy from "../components/AdminAcademy";
import AdminDashboard from "./AdminDashboard";
import { AdminInsights, AdminSpeaking } from "../components/AdminContent";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "newsletter", label: "Newsletter", icon: Users },
  { key: "contacts", label: "Contatti", icon: Mail },
  { key: "bookings", label: "Executive Call", icon: CalendarClock },
  { key: "products", label: "Prodotti", icon: Package },
  { key: "academy", label: "Academy", icon: GraduationCap },
  { key: "insights", label: "Insights", icon: Newspaper },
  { key: "speaking", label: "Speaking", icon: Mic },
];

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState(location.state?.user ? "ready" : "checking");
  const [user, setUser] = useState(location.state?.user || null);
  const [tab, setTab] = useState("dashboard");
  const [data, setData] = useState({ newsletter: [], contacts: [], bookings: [], products: [], insights: [], speaking: [] });
  const [loadingData, setLoadingData] = useState(false);
  const [productCount, setProductCount] = useState(0);

  const isAdmin = user?.is_admin;

  useEffect(() => {
    if (user) {
      setState(user.is_admin ? "ready" : "denied");
      return;
    }
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("401"); return r.json(); })
      .then((u) => { setUser(u); setState(u.is_admin ? "ready" : "denied"); })
      .catch(() => navigate("/login"));
  }, [user, navigate]);

  const loadAll = useCallback(async () => {
    setLoadingData(true);
    try {
      const [n, c, b, pr, ins, sp] = await Promise.all([
        fetch(`${API}/admin/newsletter`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/admin/contacts`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/admin/bookings`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/admin/products`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/admin/insights`, { credentials: "include" }).then((r) => r.json()),
        fetch(`${API}/admin/speaking`, { credentials: "include" }).then((r) => r.json()),
      ]);
      setData({ newsletter: n.items || [], contacts: c.items || [], bookings: b.items || [],
                products: pr.items || [], insights: ins.items || [], speaking: sp.items || [] });
      setProductCount((pr.items || []).length);
    } catch { /* ignore */ }
    setLoadingData(false);
  }, []);

  useEffect(() => {
    if (state === "ready" && isAdmin) loadAll();
  }, [state, isAdmin, loadAll]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/login");
  };

  if (state === "checking") {
    return <div className="min-h-screen flex items-center justify-center bg-[var(--paper)]"><Loader2 className="animate-spin text-[var(--petrol)]" size={40} /></div>;
  }

  if (state === "denied") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--anthracite)] text-white px-6 text-center" data-testid="admin-denied">
        <div>
          <ShieldAlert size={48} strokeWidth={1.25} className="mx-auto text-[var(--gold)] mb-6" />
          <h1 className="font-serif text-3xl mb-2">Accesso negato</h1>
          <p className="text-white/60 mb-8">Questo account non è autorizzato all'area riservata.</p>
          <button onClick={logout} data-testid="denied-logout" className="btn-fill inline-flex items-center gap-2 border border-white text-white px-6 py-3 text-xs uppercase tracking-[0.15em]" style={{ "--accent": "#ffffff", color: "#fff" }}>
            <LogOut size={15} /> Esci
          </button>
        </div>
      </div>
    );
  }

  const approve = async (id) => {
    try {
      await fetch(`${API}/admin/contacts/${id}/approve`, { method: "POST", credentials: "include" });
      loadAll();
    } catch { /* ignore */ }
  };

  const count = data[tab]?.length ?? 0;

  return (
    <div className="min-h-screen bg-[var(--paper-2)]" data-testid="admin-dashboard">
      <header className="bg-[var(--anthracite)] text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-base w-9 h-9 flex items-center justify-center border border-[var(--gold)] text-[var(--gold)]">GR</span>
            <div className="leading-none">
              <div className="font-serif text-lg">Pannello Amministratore</div>
              <div className="text-[0.6rem] uppercase tracking-[0.25em] text-white/50 mt-1">{user?.email}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white/70 hover:text-white">
            <LogOut size={15} strokeWidth={1.5} /> Esci
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 gap-4 mb-10">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} data-testid={`stat-${t.key}`} onClick={() => setTab(t.key)}
                className={`text-left border p-6 transition-[border-color,background-color] duration-300 ${tab === t.key ? "border-[var(--petrol)] bg-white" : "border-[var(--line)] bg-white/60 hover:border-[var(--petrol)]"}`}>
                <div className="flex items-center justify-between mb-4">
                  <Icon size={22} strokeWidth={1.25} className="text-[var(--petrol)]" />
                  <span className="overline !text-[var(--anthracite)]">{t.label}</span>
                </div>
                <div className="font-serif text-4xl text-[var(--ink)]">{(t.key === "dashboard" || t.key === "academy") ? "—" : (data[t.key]?.length ?? 0)}</div>
              </button>
            );
          })}
        </div>

        {/* content */}
        {tab === "dashboard" ? (
          <AdminDashboard />
        ) : tab === "products" ? (
          <AdminProducts onCount={setProductCount} />
        ) : tab === "academy" ? (
          <AdminAcademy />
        ) : tab === "insights" ? (
          <AdminInsights />
        ) : tab === "speaking" ? (
          <AdminSpeaking />
        ) : (
        <div className="bg-white border border-[var(--line)]">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--line)]">
            <h2 className="font-serif text-2xl text-[var(--ink)]">{TABS.find((t) => t.key === tab).label} <span className="text-[var(--ink-soft)] text-base">({count})</span></h2>
            {loadingData && <Loader2 className="animate-spin text-[var(--petrol)]" size={18} />}
          </div>

          {count === 0 && !loadingData ? (
            <div className="px-6 py-16 text-center text-[var(--ink-soft)]" data-testid="admin-empty">Nessun elemento al momento.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`table-${tab}`}>
                <tbody>
                  {data[tab].map((row, i) => (
                    <tr key={i} className="border-b border-[var(--line)] last:border-0 hover:bg-[var(--paper-2)]">
                      <td className="px-6 py-4 align-top">
                        <Row tab={tab} row={row} onApprove={approve} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

const fmt = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }); } catch { return s; }
};

function Row({ tab, row, onApprove }) {
  if (tab === "newsletter") {
    return (
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-semibold text-[var(--ink)]">{row.email}</span>
        {row.name && <span className="text-[var(--ink-soft)]">{row.name}</span>}
        <span className="ml-auto text-xs text-[var(--ink-soft)]">{fmt(row.created_at)}</span>
      </div>
    );
  }
  if (tab === "contacts") {
    return (
      <div>
        <div className="flex flex-wrap items-baseline gap-x-4">
          <span className="font-semibold text-[var(--ink)]">{row.name}</span>
          <a href={`mailto:${row.email}`} className="text-[var(--accent)] link-underline">{row.email}</a>
          {row.company && <span className="text-[var(--ink-soft)]">· {row.company}</span>}
          <span className="ml-auto text-xs text-[var(--ink-soft)]">{fmt(row.created_at)}</span>
        </div>
        {row.subject && <div className="mt-1 text-[var(--ink)] font-medium">{row.subject}</div>}
        <p className="mt-1 text-[var(--ink-soft)]">{row.message}</p>
      </div>
    );
  }
  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-4">
        <span className="font-semibold text-[var(--ink)]">{row.name}</span>
        <a href={`mailto:${row.email}`} className="text-[var(--accent)] link-underline">{row.email}</a>
        {row.company && <span className="text-[var(--ink-soft)]">· {row.company}</span>}
        {row.role && <span className="text-[var(--ink-soft)]">· {row.role}</span>}
        <span className="ml-auto text-xs text-[var(--ink-soft)]">{fmt(row.created_at)}</span>
      </div>
      <div className="mt-1 text-[var(--ink)]"><span className="font-medium">Tema:</span> {row.topic}</div>
      {row.preferred_time && <div className="text-[var(--ink-soft)] text-xs mt-0.5">Orario preferito: {row.preferred_time}</div>}
      {row.message && <p className="mt-1 text-[var(--ink-soft)]">{row.message}</p>}
      {(row.topic || "").includes("Candidatura") && (
        row.status === "approved" ? (
          <span data-testid={`approved-${row.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-emerald-700">
            <CheckCircle2 size={14} /> Candidatura approvata
          </span>
        ) : (
          <button data-testid={`approve-${row.id}`} onClick={() => onApprove?.(row.id)}
            className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-[var(--petrol)] text-[var(--petrol)] px-3 py-1.5 hover:bg-[var(--petrol)] hover:text-white transition-[background-color,color]">
            <CheckCircle2 size={14} /> Approva candidatura
          </button>
        )
      )}
    </div>
  );
}
